import {useRef, useEffect, MutableRefObject} from 'react';

/**
 * Hook that returns a ref containing the latest value of the provided input.
 * Useful for accessing current values inside memoized callbacks without
 * causing re-renders or recreating the callbacks.
 *
 * @example
 * ```tsx
 * const isPendingRef = useLatestValue(isPending);
 *
 * const callback = useCallback(() => {
 *   if (isPendingRef.current) {
 *     // Always reads the latest value
 *     return;
 *   }
 *   // ... do something
 * }, []); // No need to include isPending in deps
 * ```
 */
export function useLatestValue<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
}
